import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import API from '../api/axios';

const ROLE_LABELS = {
  college_admin: 'College Admin',
  coe: 'COE',
  hod: 'HOD',
  moderator: 'Moderator',
  subject_matter_expert: 'Subject Matter Expert',
  examiner: 'Examiner',
};

const needsCollege = (r) => ['college_admin', 'hod', 'moderator', 'subject_matter_expert', 'examiner'].includes(r);
const needsDepartment = (r) => ['hod', 'moderator', 'subject_matter_expert', 'examiner'].includes(r);

/**
 * Add User modal for tenant admins (university_admin / college_admin).
 * Creates a user via POST /tenants/:slug/users (temp password emailed; the user
 * resets it on first login). College admins have their college fixed.
 */
export default function AddTenantUserModal({ tenantSlug, tenantType, isCollegeAdmin = false, lockCollegeId = null, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', college_id: '', department_id: '' });
  const [colleges, setColleges] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const roles = useMemo(() => {
    if (isCollegeAdmin) return ['hod', 'moderator', 'subject_matter_expert', 'examiner'];
    return tenantType === 'university'
      ? ['college_admin', 'coe', 'hod', 'moderator', 'subject_matter_expert', 'examiner']
      : ['college_admin', 'hod', 'moderator', 'subject_matter_expert', 'examiner'];
  }, [isCollegeAdmin, tenantType]);

  const nc = needsCollege(form.role);
  const nd = needsDepartment(form.role);

  useEffect(() => {
    if (!isCollegeAdmin) {
      API.get('/colleges').then(({ data }) => { if (data.success) setColleges(data.colleges || []); }).catch(() => {});
    }
    API.get('/departments').then(({ data }) => { if (data.success) setDepartments(data.departments || []); }).catch(() => {});
  }, [isCollegeAdmin]);

  // College admin's college is fixed; for university admin use the chosen college.
  const effectiveCollegeId = isCollegeAdmin ? lockCollegeId : (form.college_id ? Number(form.college_id) : null);
  const deptOptions = departments.filter(d => !effectiveCollegeId || d.college_id === effectiveCollegeId);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'role') setForm(f => ({ ...f, role: value, department_id: '' }));
    else if (name === 'college_id') setForm(f => ({ ...f, college_id: value, department_id: '' }));
    else setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.role) return setError('Please select a role');
    if (!form.name.trim() || !form.email.trim()) return setError('Name and email are required');
    if (nc && !isCollegeAdmin && !form.college_id) return setError('Please select a college for this role');
    if (nd && !form.department_id) return setError('Please select a department for this role');

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || null,
        role: form.role,
        department_id: form.department_id || null,
      };
      if (!isCollegeAdmin && form.college_id) payload.college_id = form.college_id;
      const { data } = await API.post(`/tenants/${tenantSlug}/users`, payload);
      if (data.success) onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--bg-primary)', borderRadius: '0.75rem', padding: '2rem', maxWidth: 520, width: '100%', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--border-color)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)' }}>Add User</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-tertiary)', lineHeight: 1 }}>×</button>
        </div>

        {error && <div className="form-error" style={{ marginBottom: '1rem', color: 'var(--danger)' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Role *</label>
            <select name="role" className="form-select" value={form.role} onChange={handleChange} required>
              <option value="">Select Role</option>
              {roles.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input type="text" name="name" className="form-input" value={form.name} onChange={handleChange} placeholder="Full name" required />
            </div>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input type="email" name="email" className="form-input" value={form.email} onChange={handleChange} placeholder="Email" required />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Phone</label>
            <input type="tel" name="phone" className="form-input" value={form.phone} onChange={handleChange} placeholder="Phone number" />
          </div>

          {nc && !isCollegeAdmin && (
            <div className="form-group">
              <label className="form-label">College *</label>
              <select name="college_id" className="form-select" value={form.college_id} onChange={handleChange} required>
                <option value="">Select College</option>
                {colleges.map(c => <option key={c.college_id} value={c.college_id}>{c.college_name}</option>)}
              </select>
            </div>
          )}

          {nd && (
            <div className="form-group">
              <label className="form-label">Department *</label>
              <select name="department_id" className="form-select" value={form.department_id} onChange={handleChange} required disabled={!isCollegeAdmin && !form.college_id}>
                <option value="">Select Department</option>
                {deptOptions.map(d => <option key={d.department_id} value={d.department_id}>{d.department_name}</option>)}
              </select>
            </div>
          )}

          <div style={{ background: 'var(--info-light, #e0f2fe)', color: 'var(--info, #0369a1)', borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.85rem', margin: '0.5rem 0 1rem' }}>
            Temporary password set to <strong>Test@123</strong>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
